import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Heart, Camera, Newspaper } from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND_URL } from "../lib/api";
import { useNewsWall, deviceId } from "./NewsWall";

function FeedCard({ slug, c, onChange }) {
  const [pop, setPop] = useState(false);
  async function toggle() {
    setPop(true);
    setTimeout(() => setPop(false), 300);
    onChange({ ...c, mine: !c.mine, hearts: c.hearts + (c.mine ? -1 : 1) });
    try {
      const { data } = await api.post(`/public/weddings/${slug}/memories/${c.item_id}/heart`, { device_id: deviceId() });
      onChange({ ...c, mine: data.hearted, hearts: data.hearts });
    } catch { onChange(c); toast.error("Couldn't save your heart. Try again"); }
  }
  return (
    <article className="bg-white rounded-2xl overflow-hidden border border-[var(--line)]" data-testid={`feed-card-${c.item_id}`}>
      <img src={`${BACKEND_URL}${c.card_url}`} alt={c.headline} className="w-full aspect-square object-cover bg-black" loading="lazy" onDoubleClick={() => !c.mine && toggle()} />
      <div className="flex items-center justify-between px-4 py-3">
        <p className="text-xs text-[var(--muted)]">Photo by {c.by}</p>
        <button onClick={toggle} className="flex items-center gap-1.5 text-sm" data-testid={`feed-heart-${c.item_id}`} aria-pressed={c.mine}>
          <Heart className={`w-6 h-6 transition-transform duration-300 ${pop ? "scale-125" : "scale-100"} ${c.mine ? "fill-[#d41a24] text-[#d41a24]" : "text-[var(--ink)]"}`} />
          <span data-testid={`feed-hearts-${c.item_id}`}>{c.hearts}</span>
        </button>
      </div>
    </article>
  );
}

export default function NewsFeed() {
  const { slug } = useParams();
  const [wall, setWall] = useNewsWall(slug, 15000);
  const [sort, setSort] = useState("new");
  const [snap, setSnap] = useState({});
  if (wall?.missing) return <div className="min-h-screen flex items-center justify-center font-serif text-3xl" data-testid="feed-not-found">Wedding not found</div>;
  // "Most loved" uses heart counts frozen at the moment the sort was picked, so cards don't jump while tapping
  const pick = (k) => { setSort(k); setSnap(Object.fromEntries((wall?.cards || []).map((c) => [c.item_id, c.hearts]))); };
  const cards = [...(wall?.cards || [])].sort((a, b) => (sort === "loved" ? (snap[b.item_id] ?? 0) - (snap[a.item_id] ?? 0) : 0));
  const update = (n) => setWall((w) => ({ ...w, cards: w.cards.map((x) => (x.item_id === n.item_id ? n : x)) }));
  return (
    <div className="min-h-screen bg-[var(--bg)] px-4 py-8" data-testid="news-feed">
      <div className="max-w-md mx-auto space-y-5">
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#d41a24] font-bold flex items-center gap-1.5"><Newspaper className="w-3.5 h-3.5" /> Live from the party</p>
          <h1 className="font-serif font-light text-4xl mt-1">{wall?.couple_names || "Wedding"} News</h1>
          <p className="text-sm text-[var(--muted)] mt-1">Tap the heart on your favourites. The most-loved cards play more often on the big screen.</p>
        </div>
        <div className="flex items-center gap-2">
          {[["new", "Newest"], ["loved", "Most loved"]].map(([k, l]) => <button key={k} onClick={() => pick(k)} className={`px-4 py-1.5 rounded-full text-xs border ${sort === k ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "border-[var(--line)]"}`} data-testid={`feed-sort-${k}`}>{l}</button>)}
          <Link to={`/site/${slug}/upload`} className="btn-gold ml-auto !py-2 text-xs" data-testid="feed-upload-link"><Camera className="w-3.5 h-3.5" /> Add yours</Link>
        </div>
        {!wall && <p className="text-center text-sm text-[var(--muted)] py-16" data-testid="feed-loading">Loading the latest news…</p>}
        {wall?.error && !cards.length && <p className="text-center text-sm text-[var(--muted)] py-16" data-testid="feed-error">We couldn't load the news. We'll keep trying.</p>}
        {wall && !wall.error && !cards.length && <p className="font-serif text-2xl text-center text-[var(--muted)] py-16" data-testid="feed-empty">No breaking news yet. Be the first!</p>}
        {cards.map((c) => <FeedCard key={c.item_id} slug={slug} c={c} onChange={update} />)}
      </div>
    </div>
  );
}
